export interface VideoInfo {
  platform: 'youtube' | 'vimeo' | 'loom' | 'unknown';
  videoId: string;
  thumbnailUrl?: string;
  embedUrl?: string;
}

/**
 * Parse a video URL and extract platform and video ID
 */
export function parseVideoUrl(url: string): VideoInfo {
  const trimmedUrl = url.trim();
  
  // YouTube patterns
  const youtubePatterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/,
  ];
  
  for (const pattern of youtubePatterns) {
    const match = trimmedUrl.match(pattern);
    if (match) {
      const videoId = match[1];
      return {
        platform: 'youtube',
        videoId,
        thumbnailUrl: `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`,
        embedUrl: `https://www.youtube.com/embed/${videoId}`,
      };
    }
  }
  
  // Vimeo patterns
  const vimeoMatch = trimmedUrl.match(/(?:vimeo\.com\/)(\d+)/);
  if (vimeoMatch) {
    const videoId = vimeoMatch[1];
    return {
      platform: 'vimeo',
      videoId,
      embedUrl: `https://player.vimeo.com/video/${videoId}`,
    };
  }
  
  // Loom patterns
  const loomMatch = trimmedUrl.match(/(?:loom\.com\/share\/)([a-zA-Z0-9]+)/);
  if (loomMatch) {
    const videoId = loomMatch[1];
    return {
      platform: 'loom',
      videoId,
      thumbnailUrl: `https://cdn.loom.com/sessions/thumbnails/${videoId}-with-play.gif`,
      embedUrl: `https://www.loom.com/embed/${videoId}`,
    };
  }
  
  return { platform: 'unknown', videoId: '' };
}

/**
 * Generate responsive embed HTML for a video
 */
export function generateEmbedHtml(info: VideoInfo, title?: string): string {
  if (!info.embedUrl) return '';
  
  const titleAttr = title ? `title="${title}"` : 'title="Embedded video"';
  
  return `<div style="position: relative; padding-bottom: 56.25%; height: 0; overflow: hidden; max-width: 100%; border-radius: 8px; margin: 1rem 0;">
  <iframe 
    src="${info.embedUrl}" 
    ${titleAttr}
    style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; border: 0;"
    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
    allowfullscreen>
  </iframe>
</div>`;
}

/**
 * Get display label for a video platform
 */
export function getPlatformLabel(platform: VideoInfo['platform']): string {
  switch (platform) {
    case 'youtube':
      return 'YouTube';
    case 'vimeo':
      return 'Vimeo';
    case 'loom':
      return 'Loom';
    default:
      return 'Video';
  }
}
